import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

function WhatsAppMockup() {
  return (
    <div className="relative mx-auto w-[270px]">
      {/* Glow */}
      <div className="absolute inset-0 -z-10 blur-3xl opacity-20 bg-[#38BDF8] rounded-full scale-110" />
      {/* Phone frame */}
      <div className="w-[270px] bg-white rounded-[38px] border-[6px] border-gray-800 shadow-2xl overflow-hidden">
        {/* Status bar */}
        <div className="bg-gray-800 h-7 flex items-center justify-center">
          <div className="w-20 h-3.5 bg-gray-700 rounded-full" />
        </div>
        {/* WhatsApp header */}
        <div className="bg-[#075E54] px-3 py-2.5 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center text-white text-xs font-bold">A</div>
          <div>
            <p className="text-white text-xs font-semibold">AEROBIC</p>
            <p className="text-green-200 text-[10px]">Asistente IA · en línea</p>
          </div>
        </div>
        {/* Chat area */}
        <div className="bg-[#ECE5DD] px-2.5 py-3 space-y-2 min-h-[320px]">
          {/* User msg */}
          <div className="flex justify-end">
            <div className="bg-[#DCF8C6] text-gray-800 text-[11px] px-3 py-1.5 rounded-tl-xl rounded-bl-xl rounded-tr-sm rounded-br-xl max-w-[170px] shadow-sm">
              Hola! Quiero info sobre los planes del gimnasio
              <span className="text-[9px] text-gray-400 ml-1">9:02</span>
            </div>
          </div>
          {/* Bot msg */}
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 text-[11px] px-3 py-2 rounded-tr-xl rounded-br-xl rounded-tl-sm rounded-bl-xl max-w-[185px] shadow-sm">
              <p className="font-semibold text-[#25D366] text-[10px] mb-0.5">IA AEROBIC</p>
              Hola! Tenemos planes desde $800/mes con acceso libre a todas las sedes. ¿Te interesa el plan individual o familiar?
              <span className="text-[9px] text-gray-400 ml-1">9:02</span>
            </div>
          </div>
          {/* User msg 2 */}
          <div className="flex justify-end">
            <div className="bg-[#DCF8C6] text-gray-800 text-[11px] px-3 py-1.5 rounded-tl-xl rounded-bl-xl rounded-tr-sm rounded-br-xl max-w-[160px] shadow-sm">
              Quiero hablar con un asesor
              <span className="text-[9px] text-gray-400 ml-1">9:04</span>
            </div>
          </div>
          {/* Bot msg 2 */}
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 text-[11px] px-3 py-2 rounded-tr-xl rounded-br-xl rounded-tl-sm rounded-bl-xl max-w-[185px] shadow-sm">
              <p className="font-semibold text-[#25D366] text-[10px] mb-0.5">IA AEROBIC</p>
              Perfecto! Ya te asigno con un asesor. En breve recibiras una notificacion con el link a tu conversacion.
              <span className="text-[9px] text-gray-400 ml-1">9:04</span>
            </div>
          </div>
          {/* Status */}
          <div className="flex justify-center">
            <span className="bg-[#38BDF8]/20 text-[#0EA5E9] text-[10px] px-2.5 py-1 rounded-full font-medium">
              Derivado a asesor · Luciana M.
            </span>
          </div>
        </div>
        {/* Input bar */}
        <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
          <div className="flex-1 bg-white rounded-full h-7 px-3 flex items-center">
            <span className="text-gray-400 text-[10px]">Escribe un mensaje...</span>
          </div>
          <div className="w-7 h-7 bg-[#25D366] rounded-full flex items-center justify-center">
            <ArrowRight size={12} className="text-white" />
          </div>
        </div>
        {/* Home bar */}
        <div className="bg-gray-800 h-5 flex items-end justify-center pb-1">
          <div className="w-20 h-1 bg-gray-600 rounded-full" />
        </div>
      </div>
      {/* Floating badge */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute -right-4 top-20 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-lg"
      >
        <p className="text-xs font-semibold text-gray-900">Respuesta IA</p>
        <p className="text-[10px] text-gray-500">en 0.8 segundos</p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.3, duration: 0.5 }}
        className="absolute -left-4 bottom-32 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-lg"
      >
        <p className="text-xs font-semibold text-gray-900">Disponible</p>
        <p className="text-[10px] text-gray-500">24 hs · 7 dias</p>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const highlights = [
    "Respuesta inmediata 24/7",
    "Derivacion inteligente a asesores",
    "Integración con ABC EVO",
  ];

  return (
    <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 hero-gradient overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(#111827 1px, transparent 1px), linear-gradient(to right, #111827 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="space-y-6"
          >
            {/* Tag */}
            <motion.div variants={fadeUp}>
              <span className="inline-flex items-center gap-2 bg-sky-50 border border-sky-200 text-[#0EA5E9] text-xs font-semibold px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-[#38BDF8] rounded-full animate-pulse" />
                Sistema para Gyms · artificialmente
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              data-testid={LANDING.heroTitle}
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-800 text-gray-900 tracking-tighter leading-[1.05]"
            >
              Tu gimnasio{" "}
              <span className="gradient-text">nunca</span>
              <br />
              deja de responder.
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeUp}
              data-testid={LANDING.heroSubtitle}
              className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-lg"
            >
              Un sistema integral de atención al cliente: asistente IA en
              WhatsApp, gestión de conversaciones, reportes en tiempo real,
              notificaciones automáticas e integración con tu CRM. Todo en un
              panel centralizado.
            </motion.p>

            {/* Highlights */}
            <motion.ul variants={fadeUp} className="space-y-2">
              {highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm text-gray-700">
                  <Check size={15} className="text-[#38BDF8] flex-shrink-0" />
                  {h}
                </li>
              ))}
            </motion.ul>

            {/* CTAs */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3 pt-2">
              <a
                href="#como-funciona"
                data-testid={LANDING.heroCtaPrimary}
                className="inline-flex items-center gap-2 bg-[#38BDF8] hover:bg-[#0EA5E9] text-white font-semibold px-6 py-3 rounded-lg transition-colors text-sm"
              >
                Ver cómo funciona
                <ArrowRight size={16} />
              </a>
              <a
                href="#precios"
                data-testid={LANDING.heroCtaSecondary}
                className="inline-flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 font-medium px-6 py-3 rounded-lg transition-colors text-sm"
              >
                Ver precios
              </a>
            </motion.div>
          </motion.div>

          {/* Right: WhatsApp mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex justify-center lg:justify-end"
          >
            <WhatsAppMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
